import {
    Address,
    AdjustedDistance,
    ContainerShippingMethod,
    ConvertedShipment,
    Distance,
    HugeOilTankerSeaShippingMethod,
    IdentifiedVesselShippingMethod,
    InferredVesselShippingMethod,
    LogisticsSiteMethod,
    RawDistance,
    ResolvedShippingLegEmissionEstimate,
    RoadEmissionStandard,
    RoadFuel,
    RoadMethodologyDetails,
    RoadVehicleType,
    RoRoSeaShippingMethod,
    RouteInferenceExplanation,
    RouteInferenceLegExplanation,
    SeaShippingMethod,
    ShipmentConversionMethod,
    ShipmentMethodResponse,
    ShipmentRailMethodResponse,
    ShipmentRoadMethodResponse,
    ShipmentSeaMethodResponse,
    ShippingMethod,
    ShippingRoute,
    SimpleSeaShippingMethod,
    SimpleShipmentMethod,
    SimpleShippingMethod,
    VariableFuelSeaShippingMethod,
    VariableFuelVariableLoadSeaShippingMethod,
} from '@lune-climate/lune'
import type { CalculatedRouteLocation } from '@lune-climate/lune/esm/models/CalculatedRouteLocation'
import {
    LogisticsSiteMethod as DapiLogisticsSiteMethod,
    ShippingMethod as DapiShippingMethod,
} from '@lune-fe/dapi-models'
import { Big } from 'big.js'
import { startCase } from 'lodash'
import { None, Option, Some } from 'ts-results-es'

import { getCountryNameByThreeLetterCode } from '../../../utils/utils'
import { EmissionEstimationIcons } from '../../emissionEstimationIcons'

import { Leg } from './explanationUtils'

export const FORMATTED_CARRIER_NAMES: Record<InferredVesselShippingMethod.carrier_name, string> = {
    [InferredVesselShippingMethod.carrier_name.ALIAN_A]: 'Aliança',
    [InferredVesselShippingMethod.carrier_name.ANL]: 'ANL',
    [InferredVesselShippingMethod.carrier_name.CMA_CGM]: 'CMA CGM',
    [InferredVesselShippingMethod.carrier_name.CNC]: 'CNC',
    [InferredVesselShippingMethod.carrier_name.COSCO_SHIPPING]: 'COSCO Shipping',
    [InferredVesselShippingMethod.carrier_name.CROWLEY]: 'Crowley',
    [InferredVesselShippingMethod.carrier_name.EVERGREEN]: 'Evergreen',
    [InferredVesselShippingMethod.carrier_name.GOLD_STAR_LINE]: 'Gold Star Line',
    [InferredVesselShippingMethod.carrier_name.HAIHUA_SHIPPING]: 'Haihua Shipping',
    [InferredVesselShippingMethod.carrier_name.HAMBURG_S_D]: 'Hamburg Süd',
    [InferredVesselShippingMethod.carrier_name.HAPAG_LLOYD]: 'Hapag-Lloyd',
    [InferredVesselShippingMethod.carrier_name.HMM]: 'HMM',
    [InferredVesselShippingMethod.carrier_name.ICL]: 'ICL',
    [InferredVesselShippingMethod.carrier_name.JINJIANG_SHIPPING]: 'Jinjiang Shipping',
    [InferredVesselShippingMethod.carrier_name.KAMBARA_KISEN]: 'Kambara Kisen',
    [InferredVesselShippingMethod.carrier_name.KOREA_MARINE_TRANSPORT]: 'Korea Marine Transport',
    [InferredVesselShippingMethod.carrier_name.MAERSK]: 'Maersk',
    [InferredVesselShippingMethod.carrier_name.MATSON]: 'Matson',
    [InferredVesselShippingMethod.carrier_name.MCC]: 'MCC',
    [InferredVesselShippingMethod.carrier_name.MSC]: 'MSC',
    [InferredVesselShippingMethod.carrier_name.NAMSUNG]: 'Namsung',
    [InferredVesselShippingMethod.carrier_name.ONE]: 'ONE',
    [InferredVesselShippingMethod.carrier_name.OOCL]: 'OOCL',
    [InferredVesselShippingMethod.carrier_name.PIL]: 'PIL',
    [InferredVesselShippingMethod.carrier_name.SEAGO]: 'Seago',
    [InferredVesselShippingMethod.carrier_name.SEALAND]: 'Sealand',
    [InferredVesselShippingMethod.carrier_name.SITC]: 'SITC',
    [InferredVesselShippingMethod.carrier_name.SM_LINE]: 'SM Line',
    [InferredVesselShippingMethod.carrier_name.WAN_HAI]: 'Wan Hai',
    [InferredVesselShippingMethod.carrier_name.YANG_MING]: 'Yang Ming',
    [InferredVesselShippingMethod.carrier_name.ZIM]: 'ZIM',
}

export function parseVesselInferenceDetails(details: string): Option<{ formattedVessel: string }> {
    const match = details.match(/Picked (.+) from the following vessels/)
    if (match === null) {
        return None
    }
    return Some({ formattedVessel: match[1] })
}

export function getInferenceMessage(explanation: RouteInferenceExplanation | null): Option<string> {
    if (!explanation) return None

    const hasInference =
        explanation.inferredConnectionLeg ||
        explanation.inferredSourceTransferPoint ||
        explanation.inferredDestinationTransferPoint

    return hasInference
        ? Some('Road transport leg was inferred based on the origin and destination addresses')
        : None
}

// eslint-disable-next-line complexity
export const getShippingMethodLabel = (
    name:
        | ContainerShippingMethod.vessel_type
        | SimpleShipmentMethod
        | SimpleShippingMethod
        | SimpleSeaShippingMethod
        | SeaShippingMethod['vesselType']
        | LogisticsSiteMethod,
) => {
    switch (name) {
        case ContainerShippingMethod.vessel_type.CONTAINER_SHIP: {
            return 'Container ship'
        }
        case SimpleShippingMethod.CARGO_PLANE: {
            return 'Cargo Plane'
        }
        case SimpleShippingMethod.PASSENGER_PLANE: {
            return 'Passenger Plane'
        }
        case SimpleShippingMethod.PLANE: {
            return 'Plane'
        }
        case SimpleShippingMethod.DIESEL_TRUCK: {
            return 'Truck'
        }
        case SimpleShippingMethod.TRUCK_RIGID_7_5T_ELECTRIC: {
            return 'Electric truck'
        }
        case SimpleShippingMethod.TRUCK_GENERIC_VAN_ELECTRIC: {
            return 'Electric van'
        }
        case SimpleShippingMethod.ELECTRIC_FREIGHT_TRAIN: {
            return 'Electric train'
        }
        case SimpleShippingMethod.DIESEL_FREIGHT_TRAIN: {
            return 'Diesel train'
        }
        case SimpleShippingMethod.RAIL: {
            return 'Rail'
        }
        // SEA
        case VariableFuelSeaShippingMethod.vessel_type.SEA_BULK_CARRIER_SMALL: {
            return 'Bulk carrier, small'
        }
        case VariableFuelSeaShippingMethod.vessel_type.SEA_GENERAL_CARGO_LARGE: {
            return 'General cargo, large'
        }
        case VariableFuelSeaShippingMethod.vessel_type.SEA_GENERAL_CARGO_SMALL: {
            return 'General cargo, small'
        }
        case VariableFuelSeaShippingMethod.vessel_type.SEA_ROPAX: {
            return 'RoPax'
        }
        case VariableFuelSeaShippingMethod.vessel_type.SEA_OIL_TANKER_LARGE: {
            return 'Oil tanker, large'
        }
        case VariableFuelSeaShippingMethod.vessel_type.SEA_OIL_TANKER_MEDIUM: {
            return 'Oil tanker, medium'
        }
        case VariableFuelSeaShippingMethod.vessel_type.SEA_OIL_TANKER_SMALL: {
            return 'Oil tanker, small'
        }
        case VariableFuelVariableLoadSeaShippingMethod.vessel_type.SEA_BULK_CARRIER_LARGE: {
            return 'Bulk carrier, large'
        }
        case VariableFuelVariableLoadSeaShippingMethod.vessel_type.SEA_BULK_CARRIER_MEDIUM: {
            return 'Bulk carrier, medium'
        }
        case RoRoSeaShippingMethod.vessel_type.SEA_RORO: {
            return 'RoRo'
        }
        case HugeOilTankerSeaShippingMethod.vessel_type.SEA_OIL_TANKER_HUGE: {
            return 'Oil tanker, huge'
        }
        // INLAND
        case SimpleShippingMethod.INLAND_WATERWAY_CONTAINER_VESSEL_CONVOY: {
            return 'Container vessel, convoy'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_COUPLED_CONVOY: {
            return 'Coupled convoy (163–185 m)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_CONTAINER_VESSEL_LARGE: {
            return 'Container vessel, large (135 m)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_CONTAINER_VESSEL_MEDIUM: {
            return 'Container vessel, medium (110 m)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_MOTOR_VESSEL_MEDIUM: {
            return 'Motor vessel, medium (85–110 m / 1,000–2,000 t)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_MOTOR_VESSEL_LARGE: {
            return 'Motor vessel, large (135 m / 2,000–3,000 t)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_MOTOR_VESSEL_SMALL: {
            return 'Motor vessel, small (50-80 m / 650-1,000 t)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_MOTOR_VESSEL_EXTRA_SMALL: {
            return 'Motor vessel, extra small (less than 50 m / 650 t)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_PUSHED_CONVOY_MEDIUM: {
            return 'Pushed convoy, medium (4–5 barges)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_PUSHED_CONVOY_LARGE: {
            return 'Pushed convoy, large (6 barges)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_PUSHED_CONVOY_SMALL: {
            return 'Pushed convoy, small (2 barges)'
        }
        case SimpleShippingMethod.INLAND_WATERWAY_TANKER: {
            return 'Tanker'
        }
        case LogisticsSiteMethod.MARITIME_CONTAINER_TERMINAL: {
            return 'Maritime container terminal'
        }
        case LogisticsSiteMethod.TRANSHIPMENT_SITE_AMBIENT: {
            return 'Ambient transhipment site'
        }
        case LogisticsSiteMethod.TRANSHIPMENT_SITE_CHILLED: {
            return 'Chilled transhipment site'
        }
        case LogisticsSiteMethod.TRANSHIPMENT_SITE_MIXED: {
            return 'Mixed transhipment site'
        }
        case LogisticsSiteMethod.STORAGE_AND_TRANSHIPMENT_AMBIENT: {
            return 'Ambient storage and transhipment site'
        }
        case LogisticsSiteMethod.STORAGE_AND_TRANSHIPMENT_CHILLED: {
            return 'Chilled storage and transhipment site'
        }
        case LogisticsSiteMethod.STORAGE_AND_TRANSHIPMENT_MIXED: {
            return 'Mixed storage and transhipment site'
        }
        case LogisticsSiteMethod.WAREHOUSE_AMBIENT: {
            return 'Ambient warehouse'
        }
        case LogisticsSiteMethod.WAREHOUSE_CHILLED: {
            return 'Chilled warehouse'
        }
        case LogisticsSiteMethod.WAREHOUSE_MIXED: {
            return 'Mixed warehouse'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_10DWKT_HFO: {
            return 'Bulk carrier, <10dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_10DWKT_VLSFO: {
            return 'Bulk carrier, <10dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_10DWKT_MDO: {
            return 'Bulk carrier, <10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_35DWKT_HFO: {
            return 'Bulk carrier, 10-35dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_35DWKT_VLSFO: {
            return 'Bulk carrier, 10-35dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_35DWKT_MDO: {
            return 'Bulk carrier, 10-35dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_60DWKT_HFO: {
            return 'Bulk carrier, 35-60dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_60DWKT_VLSFO: {
            return 'Bulk carrier, 35-60dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_60DWKT_MDO: {
            return 'Bulk carrier, 35-60dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_100DWKT_HFO: {
            return 'Bulk carrier, 60-100dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_100DWKT_VLSFO: {
            return 'Bulk carrier, 60-100dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_100DWKT_MDO: {
            return 'Bulk carrier, 60-100dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_200DWKT_HFO: {
            return 'Bulk carrier, 100-200dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_200DWKT_VLSFO: {
            return 'Bulk carrier, 100-200dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_200DWKT_MDO: {
            return 'Bulk carrier, 100-200dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_OVER_200DWKT_HFO: {
            return 'Bulk carrier, >200dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_OVER_200DWKT_VLSFO: {
            return 'Bulk carrier, >200dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_BULK_CARRIER_OVER_200DWKT_MDO: {
            return 'Bulk carrier, >200dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_5DWKT_HFO: {
            return 'Chemical tankerr, <5dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_5DWKT_VLSFO: {
            return 'Chemical tanker, <5dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_5DWKT_MDO: {
            return 'Chemical tanker, <5dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_10DWKT_HFO: {
            return 'Chemical tanker, 5-10dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_10DWKT_VLSFO: {
            return 'Chemical tanker, 5-10dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_10DWKT_MDO: {
            return 'Chemical tanker, 5-10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_20DWKT_HFO: {
            return 'Chemical tanker, 10-20dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_20DWKT_VLSFO: {
            return 'Chemical tanker, 10-20dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_20DWKT_MDO: {
            return 'Chemical tanker, 10-20dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_40DWKT_HFO: {
            return 'Chemical tanker, 20-40dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_40DWKT_VLSFO: {
            return 'Chemical tanker, 20-40dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_40DWKT_MDO: {
            return 'Chemical tanker, 20-40dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_OVER_40DWKT_HFO: {
            return 'Chemical tanker, >40dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_OVER_40DWKT_VLSFO: {
            return 'Chemical tanker, >40dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_CHEMICAL_TANKER_OVER_40DWKT_MDO: {
            return 'Chemical tanker, >40dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_5DWKT_HFO: {
            return 'General cargo, <5dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_5DWKT_VLSFO: {
            return 'General cargo, <5dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_5DWKT_MDO: {
            return 'General cargo, <5dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_10DWKT_HFO: {
            return 'General cargo, 5-10dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_10DWKT_VLSFO: {
            return 'General cargo, 5-10dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_10DWKT_MDO: {
            return 'General cargo, 5-10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_20DWKT_HFO: {
            return 'General cargo, 10-20dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_20DWKT_VLSFO: {
            return 'General cargo, 10-20dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_20DWKT_MDO: {
            return 'General cargo, 10-20dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_OVER_20DWKT_HFO: {
            return 'General cargo, >20dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_OVER_20DWKT_VLSFO: {
            return 'General cargo, >20dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_GENERAL_CARGO_OVER_20DWKT_MDO: {
            return 'General cargo, >20dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_50DWKT_HFO: {
            return 'Liquefied tanker, <50dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_50DWKT_VLSFO: {
            return 'Liquefied tanker, <50dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_50DWKT_MDO: {
            return 'Liquefied tanker, <50dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_100DWKT_HFO: {
            return 'Liquefied tanker, 50-100dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_100DWKT_VLSFO: {
            return 'Liquefied tanker, 50-100dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_100DWKT_MDO: {
            return 'Liquefied tanker, 50-100dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_200DWKT_HFO: {
            return 'Liquefied tanker, 100-200dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_200DWKT_VLSFO: {
            return 'Liquefied tanker, 100-200dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_200DWKT_MDO: {
            return 'Liquefied tanker, 100-200dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_OVER_200DWKT_HFO: {
            return 'Liquefied tanker, >200dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_OVER_200DWKT_VLSFO: {
            return 'Liquefied tanker, >200dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_LIQUEFIED_GAS_TANKER_OVER_200DWKT_MDO: {
            return 'Liquefied tanker, >200dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_5DWKT_HFO: {
            return 'Oil tanker, <5dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_5DWKT_VLSFO: {
            return 'Oil tanker, <5dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_5DWKT_MDO: {
            return 'Oil tanker, <5dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_10DWKT_HFO: {
            return 'Oil tanker, 5-10dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_10DWKT_VLSFO: {
            return 'Oil tanker, 5-10dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_10DWKT_MDO: {
            return 'Oil tanker, 5-10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_20DWKT_HFO: {
            return 'Oil tanker, 10-20dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_20DWKT_VLSFO: {
            return 'Oil tanker, 10-20dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_20DWKT_MDO: {
            return 'Oil tanker, 10-20dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_60DWKT_HFO: {
            return 'Oil tanker, 20-60dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_60DWKT_VLSFO: {
            return 'Oil tanker, 20-60dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_60DWKT_MDO: {
            return 'Oil tanker, 20-60dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_80DWKT_HFO: {
            return 'Oil tanker, 60-80dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_80DWKT_VLSFO: {
            return 'Oil tanker, 60-80dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_80DWKT_MDO: {
            return 'Oil tanker, 60-80dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_112DWKT_HFO: {
            return 'Oil tanker, 80-112dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_112DWKT_VLSFO: {
            return 'Oil tanker, 80-112dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_112DWKT_MDO: {
            return 'Oil tanker, 80-112dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_200DWKT_HFO: {
            return 'Oil tanker, 112-200dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_200DWKT_VLSFO: {
            return 'Oil tanker, 112-200dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_200DWKT_MDO: {
            return 'Oil tanker, 112-200dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_OVER_200DWKT_HFO: {
            return 'Oil tanker, >200dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_OVER_200DWKT_VLSFO: {
            return 'Oil tanker, >200dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OIL_TANKER_OVER_200DWKT_MDO: {
            return 'Oil tanker, >200dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OTHER_LIQUIDS_TANKER_1DWKT_HFO: {
            return 'Other liquids tanker, <1dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OTHER_LIQUIDS_TANKER_1DWKT_VLSFO: {
            return 'Other liquids tanker, <1dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OTHER_LIQUIDS_TANKER_1DWKT_MDO: {
            return 'Other liquids tanker, <1dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_OTHER_LIQUIDS_TANKER_OVER_1DWKT_HFO: {
            return 'Other liquids tanker, >1dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_OTHER_LIQUIDS_TANKER_OVER_1DWKT_VLSFO: {
            return 'Other liquids tanker, >1dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_OTHER_LIQUIDS_TANKER_OVER_1DWKT_MDO: {
            return 'Other liquids tanker, >1dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_2DWKT_HFO: {
            return 'Ro-Pax, <2dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_2DWKT_VLSFO: {
            return 'Ro-Pax, <2dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_2DWKT_MDO: {
            return 'Ro-Pax, <2dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_5DWKT_HFO: {
            return 'Ro-Pax, 2-5dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_5DWKT_VLSFO: {
            return 'Ro-Pax, 2-5dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_5DWKT_MDO: {
            return 'Ro-Pax, 2-5dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_10DWKT_HFO: {
            return 'Ro-Pax, 5-10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_10DWKT_VLSFO: {
            return 'Ro-Pax, 5-10dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_10DWKT_MDO: {
            return 'Ro-Pax, 5-10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_20DWKT_HFO: {
            return 'Ro-Pax, 10-20dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_20DWKT_VLSFO: {
            return 'Ro-Pax, 10-20dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_20DWKT_MDO: {
            return 'Ro-Pax, 10-20dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_OVER_20DWKT_HFO: {
            return 'Ro-Pax, >20dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_OVER_20DWKT_VLSFO: {
            return 'Ro-Pax, >20dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_ROPAX_OVER_20DWKT_MDO: {
            return 'Ro-Pax, >20dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_2DWKT_HFO: {
            return 'Refrigerated bulk carrier, <2dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_2DWKT_VLSFO: {
            return 'Refrigerated bulk carrier, <2dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_2DWKT_MDO: {
            return 'Refrigerated bulk carrier, <2dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_6DWKT_HFO: {
            return 'Refrigerated bulk carrier, 2-6dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_6DWKT_VLSFO: {
            return 'Refrigerated bulk carrier, 2-6dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_6DWKT_MDO: {
            return 'Refrigerated bulk carrier, 2-6dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_10DWKT_HFO: {
            return 'Refrigerated bulk carrier, 6-10dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_10DWKT_VLSFO: {
            return 'Refrigerated bulk carrier, 6-10dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_10DWKT_MDO: {
            return 'Refrigerated bulk carrier, 6-10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_OVER_10DWKT_HFO: {
            return 'Refrigerated bulk carrier, >10dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_OVER_10DWKT_VLSFO: {
            return 'Refrigerated bulk carrier, <10dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_REFRIGERATED_BULK_CARRIER_OVER_10DWKT_MDO: {
            return 'Refrigerated bulk carrier, <10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_5DWKT_HFO: {
            return 'Ro-Ro, <5dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_5DWKT_VLSFO: {
            return 'Ro-Ro, <5dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_5DWKT_MDO: {
            return 'Ro-Ro, <5dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_10DWKT_HFO: {
            return 'Ro-Ro, 5-10dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_10DWKT_VLSFO: {
            return 'Ro-Ro, 5-10dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_10DWKT_MDO: {
            return 'Ro-Ro, 5-10dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_15DWKT_HFO: {
            return 'Ro-Ro, 10-15dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_15DWKT_VLSFO: {
            return 'Ro-Ro, 10-15dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_15DWKT_MDO: {
            return 'Ro-Ro, 10-15dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_OVER_15DWKT_HFO: {
            return 'Ro-Ro, >15dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_OVER_15DWKT_VLSFO: {
            return 'Ro-Ro, >15dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_RORO_OVER_15DWKT_MDO: {
            return 'Ro-Ro, >15dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_30DWKT_HFO: {
            return 'Vehicle carrier, <30dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_30DWKT_VLSFO: {
            return 'Vehicle carrier, <30dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_30DWKT_MDO: {
            return 'Vehicle carrier, <30dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_50DWKT_HFO: {
            return 'Vehicle carrier, 30-50dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_50DWKT_VLSFO: {
            return 'Vehicle carrier, 30-50dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_50DWKT_MDO: {
            return 'Vehicle carrier, 30-50dwkt, MDO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_OVER_50DWKT_HFO: {
            return 'Vehicle carrier, >50dwkt, HFO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_OVER_50DWKT_VLSFO: {
            return 'Vehicle carrier, >50dwkt, VLSFO'
        }
        case SimpleSeaShippingMethod.SEA_VEHICLE_CARRIER_OVER_50DWKT_MDO: {
            return 'Vehicle carrier, >50dwkt, MDO'
        }
        default: {
            return getTruckShippingMethodLabel(name) ?? name
        }
    }
}

// eslint-disable-next-line complexity
export const getRoadGrandientAndSituationLabel = (
    gradient: RoadMethodologyDetails['gradient'] | undefined,
    situation: RoadMethodologyDetails['situation'] | undefined,
): string => {
    switch (`${gradient}/${situation}`) {
        case 'flat/city_urban_freeflow':
            return 'City road, flat, freeflow traffic'
        case 'hilly/city_urban_freeflow':
            return 'City road, hilly, freeflow traffic'
        case 'mountainous/city_urban_freeflow':
            return 'City road, mountainous, freeflow traffic'
        case 'flat/motorway_urban_freeflow':
            return 'Motorway, flat, freeflow traffic'
        case 'hilly/motorway_urban_freeflow':
            return 'Motorway, hilly, freeflow traffic'
        case 'mountainous/motorway_urban_freeflow':
            return 'Motorway, mountainous, freeflow traffic'
        case 'flat/motorway_rural_freeflow':
            return 'Rural motorway, flat, freeflow traffic'
        case 'hilly/motorway_rural_freeflow':
            return 'Rural motorway, hilly, freeflow traffic'
        case 'mountainous/motorway_rural_freeflow':
            return 'Rural motorway, mountainous, freeflow traffic'
        case 'undefined/city_urban_freeflow':
            return 'City road, freeflow traffic'
        case 'undefined/motorway_urban_freeflow':
            return 'Motorway, freeflow traffic'
        case 'undefined/motorway_rural_freeflow':
            return 'Rural motorway, freeflow traffic'
        case 'flat/undefined':
            return 'Flat'
        case 'hilly/undefined':
            return 'Hilly'
        case 'mountainous/undefined':
            return 'Mountainous'
        case 'flat/motorway_urban_stop_and_go':
            return 'Motorway, flat, stop and go traffic'
        case 'flat/motorway_rural_heavy_traffic':
            return 'Rural motorway, heavy traffic'
        case 'flat/motorway_rural_stop_and_go':
            return 'Rural motorway, flat, stop and go traffic'
        case 'flat/arterial_road_rural_freeflow':
            return 'Rural arterial road, flat, freeflow traffic'
        case 'flat/arterial_road_rural_heavy_traffic':
            return 'Rural arterial road, flat, heavy traffic'
        case 'flat/arterial_road_rural_stop_and_go':
            return 'Rural arterial road, flat, stop and go traffic'
        case 'hilly/motorway_urban_stop_and_go':
            return 'Rural arterial road, hilly, stop and go traffic'
        case 'hilly/motorway_rural_heavy_traffic':
            return 'Rural arterial road, hilly, heavy traffic'
        case 'hilly/motorway_rural_stop_and_go':
            return 'Rural motorway, hilly, stop and go traffic'
        case 'hilly/arterial_road_rural_freeflow':
            return 'Rural arterial road, hilly, freeflow traffic'
        case 'hilly/arterial_road_rural_heavy_traffic':
            return 'Rural arterial road, hilly, heavy traffic'
        case 'hilly/arterial_road_rural_stop_and_go':
            return 'Rural arterial road, hilly, stop and go traffic'
        case 'mountainous/motorway_urban_stop_and_go':
            return 'Motorway, mountainous, stop and go traffic'
        case 'mountainous/motorway_rural_heavy_traffic':
            return 'Rural motorway, mountainous, heavy traffic'
        case 'mountainous/motorway_rural_stop_and_go':
            return 'Rural motorway, mountainous, stop and go traffic'
        case 'mountainous/arterial_road_rural_freeflow':
            return 'Rural arterial road, mountainous, stop and go traffic'
        case 'mountainous/arterial_road_rural_heavy_traffic':
            return 'Rural arterial road, mountainous, heavy traffic'
        case 'mountainous/arterial_road_rural_stop_and_go':
            return 'Rural arterial road, mountainous, stop and go traffic'
        case 'flat/city_urban_heavy_traffic':
            return 'City road, heavy traffic'
        case 'hilly/city_urban_heavy_traffic':
            return 'City road, hilly, heavy traffic'
        case 'mountainous/city_urban_heavy_traffic':
            return 'City road, mountainous, heavy traffic'
        case 'flat/city_urban_stop_and_go':
            return 'City road, flat, stop and go'
        case 'hilly/city_urban_stop_and_go':
            return 'City road, hilly, stop and go'
        case 'mountainous/city_urban_stop_and_go':
            return 'City road, mountainous, stop and go'
    }

    throw new Error(`Unsupported gradient/situation: ${gradient} / ${situation}`)
}

export const getTradeLaneLabel = (name: string) => {
    const newName = name
        .replaceAll('disaggregated_other', 'other disaggregated')
        .replaceAll('aggregated_other', 'other aggregated')
        .replaceAll('disaggregated_', '')
        .replaceAll('aggregated_', '')
        .replaceAll('_', ' ')
    return startCase(newName)
        .replaceAll('Se', 'South East')
        .replaceAll('Ne', 'North East')
        .replaceAll('America East', 'America, East coast')
        .replaceAll('America West', 'America, West coast')
        .replaceAll('To', 'to')
}

export const getTransitCorridorLabel = (name: string) => {
    return startCase(name.replaceAll('_', ' '))
}

export const resolvedLegToApiLeg = (
    resolvedLeg: ResolvedShippingLegEmissionEstimate,
    convertedShipment: ConvertedShipment | null,
    shipmentConversionMethod: ShipmentConversionMethod | null,
    inferredRoute?: ShippingRoute,
    legMethodologyDetails?: RouteInferenceLegExplanation | null,
): Leg => {
    const { route: resolvedLegRoute, ...resolvedLegWithoutRoute } = resolvedLeg
    return {
        ...resolvedLegWithoutRoute,
        route: inferredRoute ?? undefined,
        responseRoute: resolvedLegRoute,
        convertedShipment,
        shipmentConversionMethod,
        routeInferenceDetails: legMethodologyDetails,
    }
}

/**
 * We return a variety of resolved legs this type covers both business and logistics resolved legs
 */
type ResolvedLeg = Pick<Partial<Leg>, 'routeInferenceDetails'> & {
    responseRoute?: {
        source: CalculatedRouteLocation
        legs: {
            location: CalculatedRouteLocation
            rawDistance?: RawDistance
            distance: Distance
            adjustedDistance?: AdjustedDistance
        }[]
    } | null
}

/**
 * Return the label for a resolved leg source
 * Resolved legs have a "CalculatedRoute" type, not a request route
 * and the source label is not directly returned
 */
export function getResolvedLegSource(leg: ResolvedLeg): string | null {
    return (
        leg.routeInferenceDetails?.route.source.label ??
        (leg.responseRoute?.source.label === 'Unknown' ? null : leg.responseRoute?.source.label) ??
        null
    )
}

/**
 * Return the label for a resolved leg destination
 * Resolved legs have a "CalculatedRoute" type, not a request route
 * and the destination label is not directly returned
 */
export function getResolvedLegDestination(leg: ResolvedLeg): string | null {
    const lastElement = leg.responseRoute?.legs[leg.responseRoute.legs.length - 1]
    return (
        leg.routeInferenceDetails?.route.destination.label ??
        (lastElement?.location.label === 'Unknown' ? null : lastElement?.location.label) ??
        null
    )
}

export const getRoadVehicleName = (vehicleType: RoadVehicleType | 'truck'): string => {
    switch (vehicleType) {
        case RoadVehicleType.TRUCK_GENERIC_VAN:
            return 'Van <3.5 t GVW'
        case RoadVehicleType.TRUCK_RIGID_7_5T:
            return 'Rigid truck 3.5-7.5 t GVW'
        case RoadVehicleType.TRUCK_RIGID_12T:
            return 'Rigid truck 7.5-12 t GVW'
        case RoadVehicleType.TRUCK_RIGID_20T:
            return 'Rigid truck 12-20 t GVW'
        case RoadVehicleType.TRUCK_RIGID_26T:
            return 'Rigid truck 20-26 t GVW'
        case RoadVehicleType.TRUCK_RIGID_32T:
            return 'Rigid truck 26-32 t GVW'
        case RoadVehicleType.TRUCK_ARTICULATED_34T:
            return 'Articulated truck <34 t GVW'
        case RoadVehicleType.TRUCK_ARTICULATED_40T:
            return 'Articulated truck 34-40 t GVW'
        case RoadVehicleType.TRUCK_ARTICULATED_44T:
            return 'Articulated truck 40-44 t GVW'
        case RoadVehicleType.TRUCK_ARTICULATED_60T:
            return 'Articulated truck 44-60 t GVW'
        case RoadVehicleType.TRUCK_ARTICULATED_72T:
            return 'Articulated truck 60-72 t GVW'
        case 'truck':
            return 'Truck'
    }
    throw new Error(`Unsupported vehicleType: ${vehicleType}`)
}

const TRUCK_METHOD_BASE_LABELS: Array<[string, string]> = [
    ['diesel_truck', 'Truck'],
    ['truck_generic_van', getRoadVehicleName(RoadVehicleType.TRUCK_GENERIC_VAN)],
    ['truck_generic_urban', 'Urban truck'],
    ['truck_generic_mgv', 'Medium goods vehicle'],
    ['truck_generic_hgv', 'Heavy goods vehicle'],
    ['truck_rigid_7_5t', getRoadVehicleName(RoadVehicleType.TRUCK_RIGID_7_5T)],
    ['truck_rigid_12t', getRoadVehicleName(RoadVehicleType.TRUCK_RIGID_12T)],
    ['truck_rigid_20t', getRoadVehicleName(RoadVehicleType.TRUCK_RIGID_20T)],
    ['truck_rigid_26t', getRoadVehicleName(RoadVehicleType.TRUCK_RIGID_26T)],
    ['truck_rigid_32t', getRoadVehicleName(RoadVehicleType.TRUCK_RIGID_32T)],
    ['truck_rigid_40t', 'Rigid truck 40 t GVW'],
    ['truck_articulated_34t', getRoadVehicleName(RoadVehicleType.TRUCK_ARTICULATED_34T)],
    ['truck_articulated_40t', getRoadVehicleName(RoadVehicleType.TRUCK_ARTICULATED_40T)],
    ['truck_articulated_44t', getRoadVehicleName(RoadVehicleType.TRUCK_ARTICULATED_44T)],
    ['truck_articulated_60t', getRoadVehicleName(RoadVehicleType.TRUCK_ARTICULATED_60T)],
    ['truck_articulated_72t', getRoadVehicleName(RoadVehicleType.TRUCK_ARTICULATED_72T)],
    ['truck_na_van', 'North American van'],
    ['truck_na_general', 'North American general truck'],
    ['truck_na_auto_carrier', 'North American auto carrier truck'],
    ['truck_na_dray', 'North American dray truck'],
    ['truck_na_expedited', 'North American expedited truck'],
    ['truck_na_flatbed', 'North American flatbed truck'],
    ['truck_na_heavy_bulk', 'North American heavy bulk truck'],
    ['truck_na_dry_van_ltl', 'North American dry van LTL truck'],
    ['truck_na_dry_van_tl', 'North American dry van TL truck'],
    ['truck_na_mixed', 'North American mixed truck'],
    ['truck_na_moving', 'North American moving truck'],
    ['truck_na_package', 'North American package truck'],
    ['truck_na_refrigerated', 'North American refrigerated truck'],
    ['truck_na_specialized', 'North American specialized truck'],
    ['truck_na_tanker', 'North American tanker truck'],
]

// eslint-disable-next-line complexity
const getTruckMethodQualifierLabels = (qualifierString: string): string[] => {
    const parts = qualifierString.split('_')
    const labels: string[] = []

    for (let index = 0; index < parts.length; index += 1) {
        const currentPart = parts[index]
        const nextPart = parts[index + 1]

        if (currentPart === 'light' && nextPart === 'load') {
            labels.push('light load')
            index += 1
            continue
        }

        if (currentPart === 'heavy' && nextPart === 'load') {
            labels.push('heavy load')
            index += 1
            continue
        }

        if (currentPart === 'bio' && nextPart === 'lng') {
            labels.push('bio-LNG')
            index += 1
            continue
        }

        if (currentPart === 'si' && nextPart === 'engine') {
            labels.push('SI engine')
            index += 1
            continue
        }

        switch (currentPart) {
            case 'cng':
                labels.push('CNG')
                break
            case 'lpg':
                labels.push('LPG')
                break
            case 'lng':
                labels.push('LNG')
                break
            case 'hpdi':
                labels.push('HPDI')
                break
            case 'petrol':
                labels.push('petrol')
                break
            case 'diesel':
                labels.push('diesel')
                break
            case 'electric':
                labels.push('electric')
                break
            case 'container':
                labels.push('container')
                break
            case 'refrigerated':
                labels.push('refrigerated')
                break
            default:
                labels.push(startCase(currentPart))
        }
    }

    return labels
}

const getTruckShippingMethodLabel = (name: string): string | null => {
    if (!name.startsWith('truck_') && !name.startsWith('diesel_truck')) {
        return null
    }

    const baseMethodEntry = TRUCK_METHOD_BASE_LABELS.find(([baseMethod]) => {
        return name === baseMethod || name.startsWith(`${baseMethod}_`)
    })

    if (!baseMethodEntry) {
        return null
    }

    const [baseMethod, baseLabel] = baseMethodEntry

    if (name === baseMethod) {
        return baseLabel
    }

    const qualifierLabels = getTruckMethodQualifierLabels(name.slice(baseMethod.length + 1))

    return qualifierLabels.length > 0 ? `${baseLabel} (${qualifierLabels.join(', ')})` : baseLabel
}

export const getCapacityUtilisationLabel = (
    capacityUtilisation: RoadMethodologyDetails['capacityUtilisation'],
): string => {
    return `Capacity utilisation: ${Big(capacityUtilisation).mul(100).round(2)}%`
}

export const getRoadEmissionStandard = (
    emissionStandard: RoadMethodologyDetails['emissionStandard'],
    options?: { shortForm?: boolean },
): string => {
    let name: string | undefined
    switch (emissionStandard) {
        case RoadEmissionStandard.EURO1:
            name = 'EURO 1'
            break
        case RoadEmissionStandard.EURO2:
            name = 'EURO 2'
            break
        case RoadEmissionStandard.EURO3:
            name = 'EURO 3'
            break
        case RoadEmissionStandard.EURO4:
            name = 'EURO 4'
            break
        case RoadEmissionStandard.EURO5:
            name = 'EURO 5'
            break
        case RoadEmissionStandard.EURO6:
            name = 'EURO 6'
    }
    if (!name) {
        throw new Error(`Unsupported emission standard: ${emissionStandard}`)
    }

    return options?.shortForm ? name : `${name} standard`
}

export const getLoadLabel = (name: string): string => {
    switch (name) {
        case RoRoSeaShippingMethod.load.FREIGHT_ONLY: {
            return 'Freight only'
        }
        case RoRoSeaShippingMethod.load.TRAILER_ONLY: {
            return 'Trailer only'
        }
        case RoRoSeaShippingMethod.load.TRUCK_AND_TRAILER: {
            return 'Truck and trailer'
        }
        case VariableFuelVariableLoadSeaShippingMethod.load.AVERAGE: {
            return 'Average load'
        }
        case VariableFuelVariableLoadSeaShippingMethod.load.HEAVY: {
            return 'Heavy load'
        }
        default: {
            return name
        }
    }
}

export const getLoadFactorLabel = (loadFactor: number): string => {
    return `${Big(loadFactor).mul(100)}% load factor`
}

export const getEmptyRunsFactorLabel = (emptyRunsFactor: number): string => {
    return `${Big(emptyRunsFactor).mul(100)}% empty runs`
}

// @FESTINA Check if these can be moved to a separate utils file
/**
 * Takes Address object with the expected optional fields streetLine1, streetLine2, postcode, city
 * and non-optional countryCode and returns a string of the format "123 Main St, Berlin, Germany"
 * @param address - Address object as defined in @lune-climate/lune
 */
export const formatAddress = (address: Address) => {
    const { streetLine1, streetLine2, postcode, city, countryCode } = address

    const country = countryCode && getCountryNameByThreeLetterCode(countryCode)
    const addressArray = [streetLine1, streetLine2, city, postcode, country]

    return addressArray.filter((a) => a).join(', ')
}

export const convertDDCoordinatesToDMS = (D: number, lon?: boolean) => {
    const deg = `${D}°`
    const dir = D < 0 ? (lon ? 'W' : 'S') : lon ? 'E' : 'N'
    return `${deg} ${dir}`
}

export const typeIcon = (type: string) => {
    if (type.includes('vesselNumber')) {
        return EmissionEstimationIcons.number
    }
    if (type.includes('train')) {
        return EmissionEstimationIcons.train
    }
    if (type.includes('truck')) {
        return EmissionEstimationIcons.truck
    }
    if (type.includes('plane')) {
        return EmissionEstimationIcons.plane
    }
    if (type.includes('sea') || type.includes('ship') || type.includes('inland_waterway')) {
        return EmissionEstimationIcons.ship
    }
}

// Shipping methods
export type ShippingMethodType =
    | 'SimpleShippingMethod'
    | 'SeaShippingMethod'
    | 'ContainerShippingMethod'
    | `IdentifiedVesselShippingMethod`
    | `AirShippingMethod`
    | `RoadShippingMethod`

export type LogisticsSiteMethodType = 'LogisticsSiteMethod'

type AnyShippingMethodSelection =
    | ShippingMethod
    | LogisticsSiteMethod
    | ShipmentMethodResponse
    | DapiShippingMethod
    | DapiLogisticsSiteMethod

export const isLogisticsSiteMethod = (
    selection?: AnyShippingMethodSelection,
): selection is LogisticsSiteMethod => {
    return (
        typeof selection === 'string' &&
        Object.values(LogisticsSiteMethod).includes(selection as LogisticsSiteMethod)
    )
}

export const isSimpleShippingMethod = (
    selection?: AnyShippingMethodSelection,
): selection is SimpleShippingMethod => {
    return Object.values(SimpleShippingMethod).some((value) => value === selection)
}

export const isContainerShippingMethod = (selection?: AnyShippingMethodSelection) => {
    return (
        selection instanceof Object &&
        'vesselType' in selection &&
        Object.values(ContainerShippingMethod.vessel_type).some(
            (val) => val === selection.vesselType,
        )
    )
}

export const isAirShippingMethod = (selection?: AnyShippingMethodSelection) => {
    return (
        (typeof selection === 'object' &&
            ('flightNumber' in selection || 'aircraftType' in selection)) ||
        (typeof selection === 'string' &&
            ['plane', 'cargo_plane', 'passenger_plane'].includes(selection))
    )
}

export const isAirTransportMethod = (
    selection?: ShippingMethod | LogisticsSiteMethod | ShipmentMethodResponse,
) => {
    if (!selection) {
        return false
    }
    if (isAirShippingMethod(selection)) {
        return true
    }

    if (
        typeof selection === 'string' &&
        ['plane', 'passenger_plane', 'cargo_plane'].includes(selection)
    ) {
        return true
    }

    return false
}

export const isRoadShippingMethod = (selection: AnyShippingMethodSelection) => {
    return typeof selection === 'object' && 'vehicleType' in selection && 'fuel' in selection
}

export const isSimpleShipmentMethod = (
    selection?: ShipmentMethodResponse,
): selection is SimpleShippingMethod => {
    return Object.values(SimpleShippingMethod).some((value) => value === selection)
}

export const isShipmentRoadMethod = (
    selection?: ShipmentMethodResponse,
): selection is ShipmentRoadMethodResponse => {
    return typeof selection === 'object' && 'vehicleType' in selection && 'fuel' in selection
}

export const isShipmentRailMethod = (
    selection?: ShipmentMethodResponse,
): selection is ShipmentRailMethodResponse => {
    return typeof selection === 'object' && 'trainType' in selection
}

export const isShipmentSeaMethod = (
    selection?: ShipmentMethodResponse,
): selection is ShipmentSeaMethodResponse => {
    return (
        typeof selection === 'object' &&
        ('vesselType' in selection || 'vesselImoNumber' in selection || 'vesselName' in selection)
    )
}

export const isShipmentAirShippingMethod = (selection?: ShipmentMethodResponse) => {
    return (
        typeof selection === 'object' &&
        ('flightNumber' in selection || 'aircraftType' in selection)
    )
}

export const isShipmentAirTransportMethod = (selection?: ShipmentMethodResponse) => {
    if (!selection) {
        return false
    }

    if (isShipmentAirShippingMethod(selection)) {
        return true
    }

    return (
        typeof selection === 'string' &&
        ['plane', 'passenger_plane', 'cargo_plane'].includes(selection)
    )
}

const getSimpleShippingMethodValue = (value: string): SimpleShippingMethod | null => {
    return Object.values(SimpleShippingMethod).find((method) => method === value) ?? null
}

type ShipmentRoadMethodLabel = Pick<ShipmentRoadMethodResponse, 'vehicleType' | 'fuel'> & {
    refrigerated?: boolean | null
}

const getShipmentRoadMethodSimpleShippingMethod = (
    method: ShipmentRoadMethodLabel,
): SimpleShippingMethod | null => {
    if (method.vehicleType === 'truck') {
        return method.refrigerated
            ? SimpleShippingMethod.DIESEL_TRUCK_REFRIGERATED
            : SimpleShippingMethod.DIESEL_TRUCK
    }

    const fuelSuffix =
        method.fuel === RoadFuel.GASOLINE
            ? 'petrol'
            : method.fuel === RoadFuel.CNG
              ? 'cng'
              : method.fuel === RoadFuel.LNG
                ? 'lng'
                : null
    const baseMethod = fuelSuffix ? `${method.vehicleType}_${fuelSuffix}` : method.vehicleType
    const refrigeratedMethod = method.refrigerated ? `${baseMethod}_refrigerated` : null

    return (
        (refrigeratedMethod ? getSimpleShippingMethodValue(refrigeratedMethod) : null) ??
        getSimpleShippingMethodValue(baseMethod)
    )
}

// eslint-disable-next-line complexity
export const normalizeShipmentMethodResponse = (
    method: ShipmentMethodResponse,
): ShippingMethod | LogisticsSiteMethod | null => {
    if (typeof method === 'string') {
        if (isSimpleShippingMethod(method) || isLogisticsSiteMethod(method)) {
            return method
        }

        return null
    }

    if (isShipmentRoadMethod(method)) {
        return {
            vehicleType: method.vehicleType,
            fuel: method.fuel ?? RoadFuel.AUTO_DETECT,
            ...(typeof method.refrigerated === 'boolean'
                ? { refrigerated: method.refrigerated }
                : {}),
        }
    }

    if (isShipmentRailMethod(method)) {
        return method.trainType === ShipmentRailMethodResponse.train_type.ELECTRIC_FREIGHT_TRAIN
            ? SimpleShippingMethod.ELECTRIC_FREIGHT_TRAIN
            : SimpleShippingMethod.DIESEL_FREIGHT_TRAIN
    }

    if ('aircraftType' in method) {
        return method
    }

    if ('flightNumber' in method) {
        if (method.flightNumber && method.departureOn) {
            return {
                flightNumber: method.flightNumber,
                departureOn: method.departureOn,
            }
        }

        return SimpleShippingMethod.PLANE
    }

    if (isShipmentSeaMethod(method)) {
        const refrigerated =
            typeof method.refrigerated === 'boolean' ? { refrigerated: method.refrigerated } : {}

        if (method.vesselType === 'container_ship') {
            return {
                vesselType: ContainerShippingMethod.vessel_type.CONTAINER_SHIP,
                ...refrigerated,
            }
        }

        if (typeof method.vesselType === 'string') {
            return method.vesselType
        }

        if (method.vesselImoNumber) {
            return {
                vesselImoNumber: method.vesselImoNumber,
                vesselType: ContainerShippingMethod.vessel_type.CONTAINER_SHIP,
                ...refrigerated,
            }
        }

        if (method.vesselName) {
            return {
                vesselName: method.vesselName,
                vesselType: ContainerShippingMethod.vessel_type.CONTAINER_SHIP,
                ...refrigerated,
            }
        }
    }

    return null
}

export const getShipmentMethodLabel = (method: ShipmentMethodResponse): string => {
    const normalizedMethod = normalizeShipmentMethodResponse(method)

    if (!normalizedMethod) {
        if (isShipmentSeaMethod(method)) {
            return 'Sea'
        }

        if (isShipmentAirTransportMethod(method)) {
            return 'Air'
        }

        return '--'
    }

    if (typeof normalizedMethod === 'string') {
        return getShippingMethodLabel(normalizedMethod)
    }

    if (isRoadShippingMethod(normalizedMethod)) {
        const simpleShippingMethod = getShipmentRoadMethodSimpleShippingMethod(normalizedMethod)

        return simpleShippingMethod
            ? getShippingMethodLabel(simpleShippingMethod)
            : getRoadVehicleName(normalizedMethod.vehicleType)
    }

    if (isAirShippingMethod(normalizedMethod)) {
        return 'Air'
    }

    if ('vesselType' in normalizedMethod && typeof normalizedMethod.vesselType === 'string') {
        return getShippingMethodLabel(normalizedMethod.vesselType)
    }

    return '--'
}

export const isIdentifiedVesselShippingMethod = (selection?: AnyShippingMethodSelection) => {
    const selectionKeys = Object.keys(selection as IdentifiedVesselShippingMethod)
    return selectionKeys.includes('vesselImoNumber') || selectionKeys.includes('vesselName')
}

export const getShippingMethodMainTypeFromValue = (
    selection: AnyShippingMethodSelection,
): ShippingMethodType | LogisticsSiteMethodType => {
    if (isSimpleShippingMethod(selection)) {
        return 'SimpleShippingMethod'
    }

    if (isIdentifiedVesselShippingMethod(selection)) {
        return `IdentifiedVesselShippingMethod`
    }

    if (isContainerShippingMethod(selection)) {
        return 'ContainerShippingMethod'
    }

    if (isLogisticsSiteMethod(selection)) {
        return 'LogisticsSiteMethod'
    }

    if (isAirShippingMethod(selection)) {
        return 'AirShippingMethod'
    }

    if (isRoadShippingMethod(selection)) {
        return 'RoadShippingMethod'
    }

    return 'SeaShippingMethod'
}

// DASHBOARD ONLY

export const isSimpleSeaShippingMethod = (selection?: ShippingMethod | LogisticsSiteMethod) => {
    // These functions are a bit annoying because compile time types and runtime types are inconsistent.
    // See src/views/CalculateEmissions/Logistics/ShipmentMethods.tsx, search for
    // value: {
    //     vesselType: '' as any,
    // },
    return (
        selection instanceof Object &&
        'vesselType' in selection &&
        Object.values(SimpleSeaShippingMethod).some(
            (val) => val === (selection.vesselType as unknown as SimpleSeaShippingMethod),
        )
    )
}

export const isVariableFuelSeaShippingMethod = (selection?: ShippingMethod) => {
    return (
        selection instanceof Object &&
        'vesselType' in selection &&
        Object.values(VariableFuelSeaShippingMethod.vessel_type).some(
            (val) => val === selection.vesselType,
        )
    )
}
export const isVariableFuelVariableLoadSeaShippingMethod = (selection?: ShippingMethod) => {
    return (
        selection instanceof Object &&
        'vesselType' in selection &&
        Object.values(VariableFuelVariableLoadSeaShippingMethod.vessel_type).some(
            (val) => val === selection.vesselType,
        )
    )
}
export const isRoRoSeaShippingMethod = (selection?: ShippingMethod) => {
    return (
        selection instanceof Object &&
        'vesselType' in selection &&
        Object.values(RoRoSeaShippingMethod.vessel_type).some((val) => val === selection.vesselType)
    )
}
export const isHugeOilTankerSeaShippingMethod = (selection?: ShippingMethod) => {
    return (
        selection instanceof Object &&
        'vesselType' in selection &&
        Object.values(HugeOilTankerSeaShippingMethod.vessel_type).some(
            (val) => val === selection.vesselType,
        )
    )
}
